<template>
  <div class="earthquake-report" v-loading.fullscreen.lock="loading">
    <el-card v-for="(r, idx) in reports" :key="r.eq.EarthquakeNo" shadow="never" class="report-card">
      <div class="report-header">
        <div class="report-title-row">
          <h2 class="report-title">前言（第 {{ r.eq.EarthquakeNo }} 號地震）</h2>
          <el-button
            type="success"
            size="mini"
            :loading="r.exporting"
            :disabled="exportDisabled(r)"
            icon="el-icon-download"
            @click="exportDocx(idx)"
          >
            匯出為 .docx
          </el-button>
        </div>

        <div v-if="r.introParagraphs.length" class="report-intro">
          <p v-for="(paragraph, i) in r.introParagraphs" :key="`intro-${idx}-${i}`">
            {{ paragraph }}
          </p>
        </div>
      </div>

      <div v-if="r.reportImageUri" class="report-image">
        <img :src="r.reportImageUri" alt="中央氣象署地震報告影像" />
      </div>

      <h3 v-if="!r.errorMessage" class="section-title">地震管理值(震度大於 3 級)</h3>
      <el-alert
        v-if="r.errorMessage"
        :title="r.errorMessage"
        type="error"
        :closable="false"
        class="report-alert"
      />
      <el-table
        v-if="!r.errorMessage && r.stations.length"
        :data="r.stations"
        stripe size="mini"
        class="station-table"
      >
        <el-table-column prop="stationName" label="測站名稱" min-width="140" />
        <el-table-column prop="seismicIntensity" label="震度" width="100" />
        <el-table-column prop="countyName" label="縣市" min-width="120" />
        <el-table-column prop="areaDesc" label="震度區域" min-width="200" />
      </el-table>

      <div v-else-if="!r.errorMessage && !r.loading" class="empty-hint">
        目前無震度大於 3 級的測站資料。
      </div>

      <!-- 排名表（保持你原有的 @top-instruments 流程，但 state 換成 r.*） -->
      <div style="width: fit-content; margin-top: 20px;" :ref="`rankTable1_${idx}`">
        <RankTable
          v-if="r.isDataLoaded"
          @top-instruments="ids => handleTopInstruments(idx, ids, 'rank1')"
          :default-start="r.start"
          :default-end="r.end"
        />
      </div>
      <div style="width: fit-content; margin-top: 20px;" :ref="`rankTable2_${idx}`">
        <RankTable
          v-if="r.isDataLoaded"
          @top-instruments="ids => handleTopInstruments(idx, ids, 'rank2')"
          :default-start="r.start"
          :default-end="r.end"
          :defaultTMOption="'tm_rate'"
        />
      </div>

      <!-- chartBox1：累積變位 -->
      <div style="width: fit-content; margin-top: 20px;">
        <el-card class="box-card">
          <template v-if="r.chartReplaced.chartBox1_TM && r.chartImages.chartBox1_TM">
            <img :src="r.chartImages.chartBox1_TM" alt="chartBox1_TM" style="width: 100%; border: 1px solid #ccc;" />
          </template>
          <Chart
            v-else-if="r.flat1_TM.length"
            :key="r.chartKey + '_1tm_' + idx"
            :useUrl="useUrl"
            :presetInstrumentIds="r.flat1_TM"
            :presetDateRange="[r.start, r.end]"
            :autoRun="true" :autoGroup="false" :interactive="false"
            :y-axis-fixed-map="{ mm: { min: -30, max: 30, tickInterval: 10 } }"
            @ready="payload => handleChartReady(idx, 'chartBox1_TM', payload)"
          />
        </el-card>
      </div>

      <!-- 傾斜計 (度) -->
      <div style="width: fit-content; margin-top: 20px;">
        <el-card class="box-card">
          <template v-if="r.chartReplaced.chartBox1_STI && r.chartImages.chartBox1_STI">
            <img :src="r.chartImages.chartBox1_STI" alt="chartBox1_STI" style="width: 100%; border: 1px solid #ccc;" />
          </template>
          <Chart
            v-else-if="r.flat1_STI.length"
            :key="r.chartKey + '_1sti_' + idx"
            :useUrl="useUrl"
            :presetInstrumentIds="r.flat1_STI"
            :presetDateRange="[r.start, r.end]"
            :autoRun="true" :autoGroup="false" :interactive="false"
            :y-axis-fixed-map="{ 度: { min: -1, max: 1, tickInterval: 0.25 } }"
            @ready="payload => handleChartReady(idx, 'chartBox1_STI', payload)"
          />
        </el-card>
      </div>

      <!-- chartBox2：歸零曲線 -->
      <div style="width: fit-content; margin-top: 20px;">
        <el-card class="box-card">
          <template v-if="r.chartReplaced.chartBox2_TM && r.chartImages.chartBox2_TM">
            <img :src="r.chartImages.chartBox2_TM" alt="chartBox2_TM" style="width: 100%; border: 1px solid #ccc;" />
          </template>
          <Chart
            v-else-if="r.flat2_TM.length && r.isChart1Loaded"
            :key="r.chartKey2 + '_2tm_' + idx"
            :useUrl="useUrl"
            :presetInstrumentIds="r.flat2_TM"
            :presetDateRange="[r.start, r.end]"
            :autoRun="true" :autoGroup="false" :autoCompare="true" :interactive="false"
            :y-axis-fixed-map="{ mm: { min: -12, max: 12, tickInterval: 4 } }"
            @ready="payload => handleChartReady(idx, 'chartBox2_TM', payload)"
          />
        </el-card>
      </div>

      <div style="width: fit-content; margin-top: 20px;">
        <el-card class="box-card">
          <template v-if="r.chartReplaced.chartBox2_STI && r.chartImages.chartBox2_STI">
            <img :src="r.chartImages.chartBox2_STI" alt="chartBox2_STI" style="width: 100%; border: 1px solid #ccc;" />
          </template>
          <Chart
            v-else-if="r.flat2_STI.length && r.isChart1Loaded"
            :key="r.chartKey2 + '_2sti_' + idx"
            :useUrl="useUrl"
            :presetInstrumentIds="r.flat2_STI"
            :presetDateRange="[r.start, r.end]"
            :autoRun="true" :autoGroup="false" :autoCompare="true" :interactive="false"
            :y-axis-fixed-map="{ 度: { min: -0.3, max: 0.3, tickInterval: 0.1 } }"
            @ready="payload => handleChartReady(idx, 'chartBox2_STI', payload)"
          />
        </el-card>
      </div>

      <!-- chartBox3：1小時速率 -->
      <div style="width: fit-content; margin-top: 20px;">
        <el-card class="box-card">
          <template v-if="r.chartReplaced.chartBox3_TM && r.chartImages.chartBox3_TM">
            <img :src="r.chartImages.chartBox3_TM" alt="chartBox3_TM" style="width: 100%; border: 1px solid #ccc;" />
          </template>
          <Chart
            v-else-if="r.flat2_TM.length && r.isChart2Loaded"
            :key="r.chartKey + '_3tm_' + idx"
            :useUrl="useUrl"
            :presetInstrumentIds="r.flat2_TM"
            :presetDateRange="[r.start, r.end]"
            :autoRun="true" :autoGroup="false" :interactive="false" :autoVelocity1Hr="true"
            @ready="payload => handleChartReady(idx, 'chartBox3_TM', payload)"
          />
        </el-card>
      </div>

      <div style="width: fit-content; margin-top: 20px;">
        <el-card class="box-card">
          <template v-if="r.chartReplaced.chartBox3_STI && r.chartImages.chartBox3_STI">
            <img :src="r.chartImages.chartBox3_STI" alt="chartBox3_STI" style="width: 100%; border: 1px solid #ccc;" />
          </template>
          <Chart
            v-else-if="r.flat2_STI.length && r.isChart2Loaded"
            :key="r.chartKey + '_3sti_' + idx"
            :useUrl="useUrl"
            :presetInstrumentIds="r.flat2_STI"
            :presetDateRange="[r.start, r.end]"
            :autoRun="true" :autoGroup="false" :interactive="false" :autoVelocity1Hr="true"
            @ready="payload => handleChartReady(idx, 'chartBox3_STI', payload)"
          />
        </el-card>
      </div>

    </el-card>
  </div>
</template>

<script>
import axios from 'axios';
import { THSR_PROJECT_ID } from '@/lib/base/project';
import RankTable from '@/components/rank/RankTable.vue';
import Chart from '@/components/chart/index.vue';
import html2canvas from 'html2canvas';
import {
  AlignmentType,
  BorderStyle,
  Document,
  HeadingLevel,
  ImageRun,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  VerticalAlign,
  WidthType,
  ShadingType,
} from 'docx';
const API_ENDPOINT = 'https://opendata.cwa.gov.tw/api/v1/rest/datastore/E-A0015-001';
const API_AUTHORIZATION = 'CWB-F9D03D48-7B12-4428-9F33-CDF5E9D1B162';
export default {
  name: 'EarthquakeReportComponent',
  components: {
    RankTable,
    Chart,
  },
  data() {
    return {
      loading: false,
      instrumentList: [],
      reports: [],
      limitEarthquake: 3,
    };
  },
  created() {
    this.fetchEarthquakeData();
  },
  async mounted() {
    this.loading = true;
    await this.getProject();

    const waitUntilLoaded = () => {
      return new Promise(resolve => {
        const interval = setInterval(() => {
          // ⭐ 檢查所有報告 r 是否 chartReadyCount >= 6
          const allReady = this.reports.every(r => r.chartReadyCount >= 6);
          if (allReady) {
            clearInterval(interval);
            resolve();
          }
        }, 500);
      });
    };

    await waitUntilLoaded();
    this.loading = false;   // ⭐ 這裡才會被觸發
  },
  methods: {
    exportDisabled(r) {
      return (
        this.loading ||
        r.exporting ||
        (!r.introParagraphs.length && !r.stations.length)
      );
    },
    createReportState(earthquake) {
      const introParagraphs = this.buildIntroParagraphs(earthquake);
      const stations = this.extractStations(earthquake);

      // 計算 start / end（沿用你原本的邏輯）
      const originTimeStr = this.normalizeString(earthquake?.EarthquakeInfo?.OriginTime);
      let start = '';
      let end = '';
      if (originTimeStr) {
        const eqTime = new Date(originTimeStr);
        const startDate = new Date(eqTime);
        startDate.setDate(startDate.getDate() - 1);
        startDate.setHours(0, 0, 0, 0);

        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 2);
        endDate.setMinutes(endDate.getMinutes() - 30);

        start = this.$moment(startDate).format('YYYY-MM-DD HH:mm:ss');
        end = this.$moment(endDate).format('YYYY-MM-DD HH:mm:ss');
      }

      return {
        // 原始地震資料
        eq: earthquake,

        // 這組報告自己的顯示資料
        introParagraphs,
        stations,
        start,
        end,
        reportImageUri: this.normalizeString(earthquake?.ReportImageURI),
        errorMessage: '',
        loading: false,
        exporting: false,

        // Ranking / Chart 相關 state（**每一報告一份**）
        isChart1Loaded: false,
        isChart2Loaded: false,
        isDataLoaded: this.isDataLoaded, // 先用 global，等 getProject 完成再全部設 true
        flat1: [],
        flat2: [],
        flat1_TM: [],
        flat1_STI: [],
        flat2_TM: [],
        flat2_STI: [],
        chartKey: 0,
        chartKey2: 0,
        chartReadyCount: 0,
        chartImages: {
          chartBox1_TM: null,
          chartBox1_STI: null,
          chartBox2_TM: null,
          chartBox2_STI: null,
          chartBox3_TM: null,
          chartBox3_STI: null,
        },
        chartReplaced: {
          chartBox1_TM: false,
          chartBox1_STI: false,
          chartBox2_TM: false,
          chartBox2_STI: false,
          chartBox3_TM: false,
          chartBox3_STI: false,
        },
      };
    },
    async getProject() {
      await this.$axios({
        category: 'project',
        method: 'get',
        apiName: `${THSR_PROJECT_ID}/area-station-instrument`,
      })
        .then(res => {
          this.projectData = res.data;
          res.data.area.forEach(area => {
            area.station.forEach(station => {
              station.instrument.forEach(item => {
                this.instrumentList.push(item);
              });
            });
          });
        })
        .catch(e => console.error(e))
        .finally(() => {
          this.isDataLoaded = true;
          // ⭐ 每一組報告也標記成「資料就緒」
          this.reports.forEach(r => {
            r.isDataLoaded = true;
          });
        });
    },
    async handleChartReady(reportIndex, chartKey, payload) {
      const r = this.reports[reportIndex];
      if (!r) return;

      const { getBase64, chartInstance } = payload || {};

      if (chartInstance) {
        await new Promise(resolve => {
          let count = 0;
          const timer = setInterval(() => {
            const hasSeries = chartInstance.series?.length > 0;
            const allVisible = hasSeries && chartInstance.series.every(s => s.data?.length > 0);
            if (chartInstance.hasRendered && hasSeries && allVisible) {
              clearInterval(timer);
              resolve();
            } else if (count++ > 20) {
              clearInterval(timer);
              resolve();
            }
          }, 150);
        });
      }

      await new Promise(r => setTimeout(r, 500));

      if (typeof getBase64 === 'function') {
        try {
          const imgData = await getBase64();
          this.$set(r.chartImages, chartKey, imgData);
          this.$set(r.chartReplaced, chartKey, true);
        } catch (err) {
          console.error(`${chartKey} 擷取圖片失敗`, err);
        }
      }

      r.chartReadyCount++;
      if (r.chartReadyCount >= 2) r.isChart1Loaded = true;
      if (r.chartReadyCount >= 4) r.isChart2Loaded = true;

      this.$set(this.reports, reportIndex, { ...r });
    },
    handleTopInstruments(reportIndex, ids, source) {
      const r = this.reports[reportIndex];
      if (!r) return;

      if (source === 'rank1') {
        r.flat1_TM = ids.tm || [];
        r.flat1_STI = ids.sti || [];
        r.flat1 = [...r.flat1_TM, ...r.flat1_STI];
      } else if (source === 'rank2') {
        r.flat2_TM = ids.tm || [];
        r.flat2_STI = ids.sti || [];
        r.flat2 = [...r.flat2_TM, ...r.flat2_STI];
      }

      if (r.flat1 && r.flat1.length) r.chartKey++;
      if (r.flat2 && r.flat2.length) r.chartKey2++;

      // 確保 reactivity（雖然 Vue 2 對既有 key 直接賦值通常也沒問題）
      this.$set(this.reports, reportIndex, { ...r });
    },
    async fetchEarthquakeData() {
      this.errorMessage = '';
      try {
        const { data } = await axios.get(API_ENDPOINT, {
          params: {
            Authorization: API_AUTHORIZATION,
          },
        });
        this.processResponse(data);
      } catch (error) {
        console.error('Failed to fetch earthquake data:', error);
        this.errorMessage = '取得地震資料時發生錯誤，請稍後再試。';
      }
    },
    processResponse(payload) {
      const earthquakes = payload?.records?.Earthquake;
      if (!Array.isArray(earthquakes) || earthquakes.length === 0) {
        this.errorMessage = '目前沒有地震報告資料。';
        return;
      }

      const limited = earthquakes.slice(0, this.limitEarthquake);

      this.latestEarthquake = limited[0];

      this.reports = limited.map(eq => this.createReportState(eq));
    },
    buildIntroParagraphs(earthquake) {
      const intro = this.buildIntro(earthquake);
      return [
        ...(intro ? [intro] : []),
      ];
    },
    buildIntro(earthquake) {
      const originTime = this.normalizeString(earthquake?.EarthquakeInfo?.OriginTime);
      const eqNo = this.normalizeString(String(earthquake?.EarthquakeNo ?? ''));
      if (!originTime || !eqNo) {
        return '';
      }
      return `台灣本島於 ${originTime} 發生 ${eqNo} 號地震。`;
    },
    extractLocationName(earthquake) {
      const epicenterLocation = this.normalizeString(
        earthquake?.EarthquakeInfo?.Epicenter?.Location,
      );
      const reportContent = this.normalizeString(earthquake?.ReportContent);
      const candidates = [];
      if (reportContent) {
        const contentMatch = reportContent.match(/^(.+?)於/);
        if (contentMatch && contentMatch[1]) {
          candidates.push(contentMatch[1].trim());
        }
      }
      if (epicenterLocation) {
        const withoutParentheses = epicenterLocation.replace(/[\uFF08(].*?[\uFF09)]/g, '').trim();
        const numericIndex = withoutParentheses.search(/\d/);
        const shortened = numericIndex > -1
          ? withoutParentheses.slice(0, numericIndex).trim()
          : withoutParentheses;
        if (shortened) {
          candidates.push(shortened);
        }
      }
      return candidates.find(Boolean) || '台灣本島';
    },
    formatReportContent(content) {
      const normalized = this.normalizeString(content);
      if (!normalized) {
        return [];
      }
      const withLineBreaks = normalized.replace(/([。！？])/g, '$1\n');
      return withLineBreaks
        .split(/\n+/)
        .map(part => part.trim())
        .filter(Boolean);
    },
    extractStations(earthquake) {
      const shakingAreas = earthquake?.Intensity?.ShakingArea;
      if (!Array.isArray(shakingAreas)) {
        return [];
      }
      const collected = [];
      shakingAreas.forEach((area) => {
        const areaStations = Array.isArray(area?.EqStation) ? area.EqStation : [];
        areaStations.forEach((station) => {
          const intensityValue = this.parseIntensity(station?.SeismicIntensity);
          if (intensityValue >= 3) {
            collected.push({
              stationName: this.normalizeString(station?.StationName),
              seismicIntensity: this.normalizeString(station?.SeismicIntensity),
              countyName: this.normalizeString(area?.CountyName),
              areaDesc: this.normalizeString(area?.AreaDesc),
              intensityValue,
            });
          }
        });
      });
      collected.sort((a, b) => b.intensityValue - a.intensityValue);
      return collected.map(({ intensityValue, ...displayable }) => displayable);
    },
    parseIntensity(rawValue) {
      if (typeof rawValue !== 'string') {
        return 0;
      }
      const numericParts = rawValue.match(/\d+(?:\.\d+)?/g);
      if (!numericParts) {
        return 0;
      }
      return Math.max(...numericParts.map(Number));
    },
    normalizeString(value) {
      if (typeof value !== 'string') {
        return '';
      }
      return value;
    },
    async exportDocx(index) {
      const r = this.reports[index];
      if (!r) return;
      if (r.exporting) return;

      if (!r.eq && !r.introParagraphs.length && !r.stations.length) {
        this.$message?.warning?.('目前沒有可匯出的地震資料');
        return;
      }

      r.exporting = true;
      this.$set(this.reports, index, { ...r });

      try {
        await this.ensureExportReady(r);

        const doc = await this.buildDocxDocument(r);
        const blob = await Packer.toBlob(doc);

        const eqNo = r.eq?.EarthquakeNo ? String(r.eq.EarthquakeNo) : '';
        const filenameBase = eqNo ? `earthquake-report_${eqNo}` : 'earthquake-report';
        const timestamp = this.getExportTimestamp();
        const filename = `${filenameBase}_${timestamp}.docx`;

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);

        this.$message?.success?.(`已匯出第 ${eqNo || (index + 1)} 號地震 .docx`);
      } catch (error) {
        console.error('Failed to export earthquake docx', error);
        this.$message?.error?.('匯出失敗，請稍後再試');
      } finally {
        r.exporting = false;
        this.$set(this.reports, index, { ...r });
      }
    },
    async buildDocxDocument(report) {
      const coverChildren = this.buildCoverSection(report);
      if (!coverChildren.length) {
        coverChildren.push(this.createDocxParagraph(''));
      }

      const sections = [
        {
          properties: {
            page: {
              margin: { top: 1440, bottom: 1440, left: 1440, right: 1440 },
            },
          },
          children: coverChildren.length ? coverChildren : [this.createDocxParagraph('')],
        },
      ];

      const mainContent = await this.buildContentSection(report);
      if (mainContent.length) {
        sections.push({
          properties: {
            page: {
              margin: { top: 1440, bottom: 1440, left: 1440, right: 1440 },
            },
          },
          children: mainContent,
        });
      }

      const imageSections = await this.buildOrientationSections(report);
      sections.push(...imageSections);

      return new Document({ sections });
    },
    buildCoverSection(report) {
      const eq = report.eq || null;
      const originTime = this.normalizeString(eq?.EarthquakeInfo?.OriginTime);
      const eqNo = eq?.EarthquakeNo != null ? String(eq.EarthquakeNo) : '';
      const locationName = eq ? this.extractLocationName(eq) : '';
      const magnitudeValue = eq?.EarthquakeInfo?.EarthquakeMagnitude?.MagnitudeValue;
      const magnitudeType = this.normalizeString(eq?.EarthquakeInfo?.EarthquakeMagnitude?.MagnitudeType);
      const exportTime = this.getExportTimestamp('YYYY/MM/DD HH:mm');

      const children = [];
      children.push(this.createDocxTitleParagraph('高鐵邊坡監測\n地震後監測報告'));

      const subtitle = `${originTime}\n第${eqNo}號地震`;
      if (subtitle) {
        children.push(this.createDocxParagraph(subtitle, {
          alignment: AlignmentType.CENTER,
          size: 32,
          spacingAfter: 200,
        }));
      }

      const detailLines = [];
      if (locationName) {
        detailLines.push(`震央位置：${locationName}`);
      }
      if (magnitudeValue != null) {
        const magnitudePieces = [];
        if (magnitudeType) magnitudePieces.push(magnitudeType);
        magnitudePieces.push(String(magnitudeValue));
        detailLines.push(`規模：${magnitudePieces.join(' ')}`);
      }
      if (exportTime) {
        detailLines.push(`報表產出時間：${exportTime}`);
      }

      detailLines.forEach((line) => {
        children.push(this.createDocxParagraph(line, {
          alignment: AlignmentType.CENTER,
          size: 24,
          spacingAfter: 120,
        }));
      });

      children.push(this.createDocxParagraph('', {
        alignment: AlignmentType.CENTER,
        size: 22,
        spacingBefore: 400,
      }));

      return children;
    },
    async buildContentSection(report) {
      const children = [];

      children.push(this.createDocxHeading('前言'));

      if (report.introParagraphs.length) {
        report.introParagraphs.forEach((text) => {
          children.push(this.createDocxParagraph(text, { size: 24, spacingAfter: 200 }));
        });
      } else if (report.eq) {
        const fallbackIntro = this.buildIntro(report.eq);
        if (fallbackIntro) {
          children.push(this.createDocxParagraph(fallbackIntro, { size: 24, spacingAfter: 200 }));
        }
      }

      if (report.reportImageUri) {
        const imageBytes = await this.fetchImageBytes(
          `https://api-geoserver.geosupply.com.tw/rain/高鐵邊坡監測/${report.eq.EarthquakeNo}.png`,
        );
        if (imageBytes) {
          children.push(this.createDocxHeading('震度圖', { spacingBefore: 400 }));
          children.push(new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { after: 200 },
            children: [
              new ImageRun({
                data: imageBytes,
                transformation: { width: 640, height: 480 },
              }),
            ],
          }));
        }
      }

      children.push(this.createDocxHeading('地震管理值(震度大於 3 級)', { spacingBefore: 400 }));
      if (report.stations.length) {
        children.push(this.buildStationsDocxTable(report.stations));
      } else {
        children.push(this.createDocxParagraph('目前無震度大於 3 級的測站資料。', { size: 24 }));
      }

      children.push(this.createDocxParagraph('', {
        size: 20,
        spacingBefore: 400,
      }));

      return children;
    },
    async buildOrientationSections(report) {
      const sections = [];
      const charts = [
        { key: 'chartBox1_TM', title: '地滑計(累積變位)' },
        { key: 'chartBox1_STI', title: '邊坡滑動監測桿(累積變位)' },
        { key: 'chartBox2_TM', title: '地滑計(歸零曲線)' },
        { key: 'chartBox2_STI', title: '邊坡滑動監測桿(歸零曲線)' },
        { key: 'chartBox3_TM', title: '地滑計(1小時速率)' },
        { key: 'chartBox3_STI', title: '邊坡滑動監測桿(1小時速率)' },
      ];

      for (const chart of charts) {
        const base64 = report.chartImages[chart.key];
        if (!base64) continue;

        const bytes = this.dataURLToUint8Array(base64);
        const { width, height } = this.calculateDocImageSize(800, 450, 760, 480);
        const heading = this.createDocxHeading(chart.title, { alignment: AlignmentType.CENTER });
        const imageParagraph = new Paragraph({
          alignment: AlignmentType.CENTER,
          children: [
            new ImageRun({ data: bytes, transformation: { width, height } }),
          ],
        });

        sections.push({
          properties: { page: { size: { orientation: 'landscape' } } },
          children: [heading, imageParagraph],
        });
      }

      return sections;
    },
    createDocxTitleParagraph(text) {
      const lines = (text || '').split(/\n+/).map(line => line.trim());
      const runs = [];
      lines.forEach((line, i) => {
        if (i > 0) runs.push(new TextRun({ break: 1 }));
        runs.push(new TextRun({
          text: line,
          bold: true,
          font: 'DFKai-SB',
          size: 48,
        }));
      });

      return new Paragraph({
        children: runs,
        heading: HeadingLevel.TITLE,
        alignment: AlignmentType.CENTER,
        spacing: { before: 800, after: 400 },
      });
    },
    createDocxHeading(text, { spacingBefore = 400, spacingAfter = 200, alignment = AlignmentType.LEFT } = {}) {
      return new Paragraph({
        children: [
          new TextRun({
            text: text || '',
            bold: true,
            font: 'DFKai-SB',
            size: 28,
          }),
        ],
        spacing: { before: spacingBefore, after: spacingAfter },
        alignment,
      });
    },
    createDocxParagraph(
      text,
      { bold = false, size = 24, alignment = AlignmentType.LEFT, spacingBefore = 0, spacingAfter = 200 } = {},
    ) {
      return new Paragraph({
        children: [
          new TextRun({
            text: text || '',
            bold,
            font: 'DFKai-SB',
            size,
          }),
        ],
        spacing: { before: spacingBefore, after: spacingAfter },
        alignment,
      });
    },
    buildStationsDocxTable(stations) {
      const headerShading = { type: ShadingType.SOLID, color: 'FFFFFF', fill: '305496' };
      const rows = [
        new TableRow({
          children: [
            this.createDocxTableCell('測站名稱', { bold: true, shading: headerShading, alignment: AlignmentType.LEFT }),
            this.createDocxTableCell('震度', { bold: true, shading: headerShading }),
            this.createDocxTableCell('縣市', { bold: true, shading: headerShading }),
            this.createDocxTableCell('震度區域', { bold: true, shading: headerShading, alignment: AlignmentType.LEFT }),
          ],
        }),
        ...stations.map((station) => new TableRow({
          children: [
            this.createDocxTableCell(station.stationName || '', { alignment: AlignmentType.LEFT }),
            this.createDocxTableCell(station.seismicIntensity || ''),
            this.createDocxTableCell(station.countyName || ''),
            this.createDocxTableCell(station.areaDesc || '', { alignment: AlignmentType.LEFT }),
          ],
        })),
      ];
      return new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows,
        borders: {
          top: { style: BorderStyle.SINGLE, size: 4, color: 'BFBFBF' },
          bottom: { style: BorderStyle.SINGLE, size: 4, color: 'BFBFBF' },
          left: { style: BorderStyle.SINGLE, size: 4, color: 'BFBFBF' },
          right: { style: BorderStyle.SINGLE, size: 4, color: 'BFBFBF' },
          insideHorizontal: { style: BorderStyle.SINGLE, size: 4, color: 'D9D9D9' },
          insideVertical: { style: BorderStyle.SINGLE, size: 4, color: 'D9D9D9' },
        },
      });
    },
    createDocxTableCell(text, { bold = false, shading, alignment = AlignmentType.CENTER } = {}) {
      return new TableCell({
        children: [
          new Paragraph({
            children: [
              new TextRun({
                text: text || '',
                bold,
                font: 'DFKai-SB',
                size: 22,
              }),
            ],
            alignment,
          }),
        ],
        shading,
        verticalAlign: VerticalAlign.CENTER,
        margins: { top: 120, bottom: 120, left: 120, right: 120 },
      });
    },
    async captureRefToImage(refName, { scale = 2, backgroundColor = '#ffffff' } = {}) {
      const ref = this.$refs[refName];
      const element = Array.isArray(ref) ? ref[0] : ref;
      if (!element) return null;

      try {
        const chartNodes = [...element.querySelectorAll('[data-highcharts-chart]')];
        if (!chartNodes.length) {
          // 若沒有多個 chart，直接截整個元素
          const singleCanvas = await html2canvas(element, {
            scale,
            backgroundColor,
            useCORS: true,
            allowTaint: true,
          });
          const dataURL = singleCanvas.toDataURL('image/png');
          return {
            bytes: this.dataURLToUint8Array(dataURL),
            width: singleCanvas.width,
            height: singleCanvas.height,
          };
        }

        // 擷取所有 chart canvas
        const canvases = [];
        for (const node of chartNodes) {
          const canvas = await html2canvas(node, {
            scale,
            backgroundColor,
            useCORS: true,
            allowTaint: true,
          });
          canvases.push(canvas);
        }

        // 將所有 chart 垂直堆疊
        const maxWidth = Math.max(...canvases.map(c => c.width));
        const totalHeight = canvases.reduce((sum, c) => sum + c.height, 0);
        const mergedCanvas = document.createElement('canvas');
        mergedCanvas.width = maxWidth;
        mergedCanvas.height = totalHeight;
        const ctx = mergedCanvas.getContext('2d');
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, maxWidth, totalHeight);

        let offsetY = 0;
        for (const c of canvases) {
          const x = (maxWidth - c.width) / 2; // 置中對齊
          ctx.drawImage(c, x, offsetY);
          offsetY += c.height;
          // 若想在上下之間留空白可加這行，例如 40px：
          // offsetY += 40;
        }

        const mergedDataURL = mergedCanvas.toDataURL('image/png');
        return {
          bytes: this.dataURLToUint8Array(mergedDataURL),
          width: mergedCanvas.width,
          height: mergedCanvas.height,
        };
      } catch (error) {
        console.warn(`Failed to capture ${refName} for docx export`, error);
        return null;
      }
    },
    calculateDocImageSize(originalWidth, originalHeight, maxWidth = 520, maxHeight = 500) {
      if (!originalWidth || !originalHeight) {
        return { width: maxWidth, height: maxHeight };
      }
      const ratio = originalWidth / originalHeight;
      let width = maxWidth;
      let height = width / ratio;
      if (height > maxHeight) {
        height = maxHeight;
        width = height * ratio;
      }
      return { width, height };
    },
    dataURLToUint8Array(dataURL) {
      if (!dataURL) {
        return new Uint8Array();
      }
      const base64 = dataURL.split(',')[1] || '';
      const binary = atob(base64);
      const len = binary.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      return bytes;
    },
    async ensureExportReady(report) {
      await this.waitForCondition(() => report.isDataLoaded, 10000);
      await this.waitForCondition(
        () => report.chartReadyCount >= 6 || (!report.flat1.length && !report.flat2.length),
        10000,
      );
    },
    async waitForCondition(predicate, timeout = 10000, interval = 300) {
      const start = Date.now();
      return new Promise((resolve) => {
        const check = () => {
          if (predicate()) {
            resolve(true);
            return;
          }
          if (Date.now() - start >= timeout) {
            resolve(false);
            return;
          }
          setTimeout(check, interval);
        };
        check();
      });
    },
    async fetchImageBytes(url) {
      if (!url) {
        return null;
      }
      try {
        const response = await axios.get(url, { responseType: 'arraybuffer' });
        if (response?.data) {
          return new Uint8Array(response.data);
        }
      } catch (error) {
        console.warn('Unable to fetch report image for docx export', error);
      }
      return null;
    },
    getExportTimestamp(format = 'YYYYMMDD_HHmmss') {
      if (typeof this.$moment === 'function') {
        try {
          return this.$moment().tz('Asia/Taipei').format(format);
        } catch (error) {
          return this.$moment().format(format);
        }
      }
      const now = new Date();
      const pad = (num) => String(num).padStart(2, '0');
      return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    },
  },
};
</script>

<style scoped>
.earthquake-report {
  display: flex;
  flex-direction: column;  /* ⭐ 預設從上排到下 */
  align-items: center;     /* 置中卡片 */
  padding: 16px;
}

.report-card {
  width: 100%;
  max-width: 1440px;        /* ⭐ 限制卡片寬度避免太寬 */
  margin-bottom: 32px;     /* ⭐ 每個報告之間加間距 */
}

.report-header {
  margin-bottom: 12px;
}

.report-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.report-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px;
}

.report-intro {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 14px;
  color: #606266;
  margin-top: 8px;
}

.report-image {
  margin: 12px 0 16px;
  text-align: center;
}

.report-image img {
  max-width: 100%;
  height: auto;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 12px;
  color: #303133;
}

.station-table {
  margin-top: 12px;
}

.empty-hint {
  margin-top: 12px;
  color: #909399;
  font-size: 13px;
}

.report-alert {
  margin-bottom: 12px;
}
</style>